import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import { ArrowLeft, Download, Calendar, ChevronDown, ChevronUp, CreditCard, Banknote, Building, Receipt, Users, FileText, Scale } from 'lucide-react';
import api from '../api/axios';

const METHOD_ICONS = { CB: CreditCard, Espece: Banknote, Virement: Building, Cheque: Receipt, 'Cheque Restaurant': Receipt };
const fmtEur = (v) => (v || 0).toFixed(2);
const fmtDate = (d) => { const [y, m, day] = d.split('-'); return `${day}/${m}/${y}`; };
const fmtTime = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Cayenne' });
};

const AccountingPage = () => {
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(new Date().toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState(new Date().toISOString().slice(0, 10));
  const [expandedSessions, setExpandedSessions] = useState({});
  const [expandedZ, setExpandedZ] = useState({});
  const [tab, setTab] = useState('z'); // 'z', 'sessions', 'tva'

  useEffect(() => {
    const token = localStorage.getItem('staffToken');
    if (!token) { navigate('/staff/login'); return; }
    fetchReport();
  }, [navigate]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/payments/accounting/report?date_from=${dateFrom}&date_to=${dateTo}`);
      setReport(res.data);
    } catch { toast({ title: 'Erreur', description: 'Impossible de charger le rapport', variant: 'destructive' }); }
    finally { setLoading(false); }
  };

  const exportCSV = () => {
    if (!report) return;
    const BOM = '\uFEFF';
    const sep = ';';
    const lines = [];

    // --- HEADER ---
    lines.push('RAPPORT COMPTABLE - LA TAVERNE');
    lines.push(`Periode: du ${fmtDate(dateFrom)} au ${fmtDate(dateTo)}`);
    if (legal.name) lines.push(`Societe: ${legal.name}`);
    if (legal.siret) lines.push(`SIRET: ${legal.siret}`);
    if (legal.address) lines.push(`Adresse: ${legal.address}`);
    if (legal.forme) lines.push(`Forme juridique: ${legal.forme}`);
    if (legal.capital) lines.push(`Capital: ${legal.capital}`);
    lines.push('Regime fiscal: Exoneration TVA - Art. 294 CGI (Guyane francaise)');
    lines.push('');

    // --- SECTION 1: JOURNAL DES VENTES ---
    lines.push('=== JOURNAL DES VENTES ===');
    lines.push(['Date', 'Heure', 'N Facture', 'Table', 'Client', 'Detail articles', 'Montant (EUR)', 'Mode paiement', 'Encaisse par'].join(sep));
    (report.sessions || []).forEach(s => {
      s.payments.forEach(p => {
        const items_str = (p.items || []).map(i => `${i.quantity}x ${i.name} (${fmtEur(parseFloat(i.price) * i.quantity)})`).join(' + ');
        const time = fmtTime(p.created_at);
        const client = s.payments[0]?.items?.[0]?.customer_name || '-';
        lines.push([fmtDate(s.date), time, p.receipt_number, `T${s.table_number}`, `"${client}"`, `"${items_str}"`, fmtEur(p.amount), p.method, p.created_by_name].join(sep));
      });
    });
    lines.push('');

    // --- SECTION 2: RECAPITULATIF JOURNALIER (Z de caisse) ---
    lines.push('=== RECAPITULATIF JOURNALIER (Z DE CAISSE) ===');
    lines.push(['Date', 'Nb transactions', 'CA Net (EUR)', 'Ticket moyen (EUR)', 'Modes de paiement'].join(sep));
    Object.entries(report.daily_z || {}).sort().forEach(([day, z]) => {
      const avg = z.count > 0 ? fmtEur(z.ttc / z.count) : '0.00';
      const modes = Object.entries(z.by_method || {}).map(([m, a]) => `${m}: ${fmtEur(a)}`).join(' | ');
      lines.push([fmtDate(day), z.count, fmtEur(z.ttc), avg, `"${modes}"`].join(sep));
    });
    lines.push('');

    // --- SECTION 3: VENTILATION PAR MODE DE PAIEMENT ---
    lines.push('=== VENTILATION PAR MODE DE PAIEMENT ===');
    lines.push(['Mode', 'Nb transactions', 'Montant (EUR)', '% du CA'].join(sep));
    const totalCA = sm.total_ttc || 1;
    Object.entries(sm.by_method || {}).forEach(([m, d]) => {
      const pct = ((d.amount / totalCA) * 100).toFixed(1);
      lines.push([m, d.count, fmtEur(d.amount), `${pct}%`].join(sep));
    });
    lines.push(['TOTAL', sm.transaction_count, fmtEur(sm.total_ttc), '100.0%'].join(sep));
    lines.push('');

    // --- SECTION 4: SYNTHESE ---
    lines.push('=== SYNTHESE GENERALE ===');
    lines.push(`Chiffre d'affaires net${sep}${fmtEur(sm.total_ttc)} EUR`);
    lines.push(`Nombre de transactions${sep}${sm.transaction_count}`);
    lines.push(`Ticket moyen${sep}${fmtEur(sm.average_transaction)} EUR`);
    lines.push(`Couverts${sep}${sm.total_covers}`);
    lines.push('');
    lines.push('Exoneration de TVA - Art. 294 CGI (Guyane francaise)');
    lines.push('Tous les montants sont nets. Pas de TVA collectee ni deductible.');
    lines.push('');
    lines.push(`Document genere le ${new Date().toLocaleDateString('fr-FR', { timeZone: 'America/Cayenne' })} a ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Cayenne' })}`);

    const csv = lines.join('\n');
    const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `comptabilite_${dateFrom}_${dateTo}.csv`; a.click();
  };

  const toggleSession = (key) => setExpandedSessions(prev => ({ ...prev, [key]: !prev[key] }));
  const toggleZ = (key) => setExpandedZ(prev => ({ ...prev, [key]: !prev[key] }));

  const sm = report?.summary || {};
  const legal = report?.legal_info || {};

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f1ed] to-white print:bg-white">
      {/* Header */}
      <div className="bg-[#3d2817] text-white py-4 shadow-lg print:bg-black print:text-white">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="accounting-title">
              <Scale size={22} /> Comptabilite
            </h1>
            <p className="text-xs text-stone-300 mt-0.5">Registre fiscal - Guyane francaise (Art. 294 CGI - Exoneration TVA)</p>
          </div>
          <div className="flex gap-2 print:hidden">
            <Button onClick={() => window.print()} size="sm" className="bg-stone-600 hover:bg-stone-700 text-xs">
              <FileText size={14} className="mr-1" /> Imprimer
            </Button>
            <Button onClick={exportCSV} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-xs" data-testid="export-btn">
              <Download size={14} className="mr-1" /> Export CSV
            </Button>
            <Button onClick={() => navigate('/staff/dashboard')} size="sm" className="bg-red-600 hover:bg-red-700 text-xs">
              <ArrowLeft size={14} className="mr-1" /> Retour
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-4">
        {/* Legal header */}
        {legal.name && (
          <div className="bg-white border border-stone-200 rounded-lg p-3 mb-4 text-xs text-stone-600" data-testid="legal-header">
            <div className="flex flex-wrap gap-x-6 gap-y-1">
              <span><strong>{legal.name}</strong> {legal.forme && `- ${legal.forme}`}</span>
              {legal.siret && <span>SIRET: {legal.siret}</span>}
              {legal.tva_intra && <span>TVA Intra: {legal.tva_intra}</span>}
              {legal.ape && <span>APE: {legal.ape}</span>}
              {legal.address && <span>{legal.address}</span>}
            </div>
          </div>
        )}

        {/* Date filter */}
        <div className="flex items-end gap-3 mb-4 flex-wrap print:hidden" data-testid="date-filters">
          <div>
            <label className="text-xs text-stone-500 block mb-1">Du</label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 w-40 text-sm" data-testid="date-from" />
          </div>
          <div>
            <label className="text-xs text-stone-500 block mb-1">Au</label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 w-40 text-sm" data-testid="date-to" />
          </div>
          <Button onClick={fetchReport} size="sm" className="bg-[#2c1810] hover:bg-[#4a2c1a] h-9 text-xs" data-testid="filter-btn">
            <Calendar size={14} className="mr-1" /> Filtrer
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-20 text-stone-400">Chargement du rapport...</div>
        ) : !report ? (
          <div className="text-center py-20 text-stone-400">Aucune donnee</div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="kpi-cards">
              <Card className="border shadow-sm">
                <CardContent className="py-3 px-3">
                  <p className="text-[10px] text-stone-500 uppercase tracking-wider">Chiffre d'Affaires</p>
                  <p className="text-2xl font-bold text-[#8B0000]">{fmtEur(sm.total_ttc)}&euro;</p>
                </CardContent>
              </Card>
              <Card className="border shadow-sm">
                <CardContent className="py-3 px-3">
                  <p className="text-[10px] text-stone-500 uppercase tracking-wider">Transactions</p>
                  <p className="text-2xl font-bold text-stone-800">{sm.transaction_count}</p>
                </CardContent>
              </Card>
              <Card className="border shadow-sm">
                <CardContent className="py-3 px-3">
                  <p className="text-[10px] text-stone-500 uppercase tracking-wider">Couverts</p>
                  <p className="text-2xl font-bold text-stone-800">{sm.total_covers}</p>
                </CardContent>
              </Card>
              <Card className="border shadow-sm">
                <CardContent className="py-3 px-3">
                  <p className="text-[10px] text-stone-500 uppercase tracking-wider">Ticket Moyen</p>
                  <p className="text-2xl font-bold text-stone-800">{fmtEur(sm.average_transaction)}&euro;</p>
                </CardContent>
              </Card>
            </div>

            {/* Method breakdown + TVA summary side by side */}
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              {/* Ventilation par mode */}
              <Card className="border shadow-sm" data-testid="method-breakdown">
                <CardContent className="py-4 px-4">
                  <h3 className="text-xs font-bold text-stone-700 uppercase tracking-wider mb-3">Ventilation par mode de paiement</h3>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-stone-500 text-xs">
                        <th className="text-left py-2 font-medium">Mode</th>
                        <th className="text-right py-2 font-medium">Nb</th>
                        <th className="text-right py-2 font-medium">Montant</th>
                        <th className="text-right py-2 font-medium">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(sm.by_method || {}).map(([m, d]) => {
                        const Icon = METHOD_ICONS[m] || Receipt;
                        const pct = sm.total_ttc > 0 ? ((d.amount / sm.total_ttc) * 100).toFixed(1) : '0';
                        return (
                          <tr key={m} className="border-b border-stone-50">
                            <td className="py-2 flex items-center gap-1.5"><Icon size={14} className="text-stone-400" /> {m}</td>
                            <td className="py-2 text-right text-stone-600">{d.count}</td>
                            <td className="py-2 text-right font-semibold">{fmtEur(d.amount)}&euro;</td>
                            <td className="py-2 text-right text-stone-500">{pct}%</td>
                          </tr>
                        );
                      })}
                      <tr className="font-bold border-t-2 border-stone-300">
                        <td className="py-2">TOTAL</td>
                        <td className="py-2 text-right">{sm.transaction_count}</td>
                        <td className="py-2 text-right">{fmtEur(sm.total_ttc)}&euro;</td>
                        <td className="py-2 text-right">100%</td>
                      </tr>
                    </tbody>
                  </table>
                </CardContent>
              </Card>

              {/* Exoneration TVA */}
              <Card className="border shadow-sm border-emerald-200 bg-emerald-50/30" data-testid="tva-exemption">
                <CardContent className="py-4 px-4">
                  <h3 className="text-xs font-bold text-stone-700 uppercase tracking-wider mb-3">Regime Fiscal</h3>
                  <div className="space-y-3">
                    <div className="bg-white rounded-lg p-4 border border-emerald-200">
                      <p className="font-bold text-emerald-800 text-lg">Exoneration de TVA</p>
                      <p className="text-sm text-stone-600 mt-1">Guyane francaise - Departement d'outre-mer</p>
                    </div>
                    <div className="text-xs text-stone-500 space-y-1">
                      <p><strong>Art. 294 CGI</strong> : Les operations effectuees dans les DOM ne sont pas soumises a la TVA.</p>
                      <p><strong>Consequence</strong> : Tous les montants sont nets. Pas de TVA collectee ni deductible.</p>
                      <p><strong>Octroi de mer</strong> : Applicable selon les categories de produits (non inclus dans ce registre).</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-stone-200">
                      <p className="text-xs text-stone-500">Montant total des recettes</p>
                      <p className="text-2xl font-bold text-[#8B0000]">{fmtEur(sm.total_ttc)}&euro;</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-4 print:hidden" data-testid="tabs">
              {[
                { key: 'z', label: 'Z de Caisse', icon: FileText },
                { key: 'sessions', label: 'Sessions de table', icon: Users },
              ].map(t => (
                <Button key={t.key} size="sm" variant={tab === t.key ? 'default' : 'outline'}
                  onClick={() => setTab(t.key)}
                  className={tab === t.key ? 'bg-[#3d2817] text-white' : ''}>
                  <t.icon size={14} className="mr-1" /> {t.label}
                </Button>
              ))}
            </div>

            {/* Z de Caisse */}
            {tab === 'z' && (
              <div className="space-y-3" data-testid="z-section">
                <h3 className="text-sm font-bold text-stone-700 uppercase tracking-wider">Ticket Z - Cloture journaliere</h3>
                {Object.entries(report.daily_z || {}).map(([day, z]) => (
                  <div key={day} className="bg-white border border-stone-200 rounded-xl shadow-sm overflow-hidden">
                    <div className="flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-stone-50 transition-colors"
                      onClick={() => toggleZ(day)} data-testid={`z-day-${day}`}>
                      <div className="w-12 h-12 bg-[#3d2817] rounded-xl flex items-center justify-center text-white text-xs font-bold">
                        {fmtDate(day).slice(0, 5)}
                      </div>
                      <div className="flex-1">
                        <span className="font-semibold text-stone-800">Z du {fmtDate(day)}</span>
                        <div className="flex gap-4 text-xs text-stone-500 mt-0.5">
                          <span>{z.count} transaction{z.count > 1 ? 's' : ''}</span>
                          <span>{z.covers} couvert{z.covers > 1 ? 's' : ''}</span>
                        </div>
                      </div>
                      <div className="text-right mr-4">
                        <p className="text-lg font-bold text-[#8B0000]">{fmtEur(z.ttc)}&euro;</p>
                        <p className="text-xs text-stone-500">Net (exo. TVA)</p>
                      </div>
                      {expandedZ[day] ? <ChevronUp size={18} className="text-stone-400" /> : <ChevronDown size={18} className="text-stone-400" />}
                    </div>
                    {expandedZ[day] && (
                      <div className="border-t border-stone-100 bg-stone-50/50 p-4">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b text-stone-500">
                              <th className="text-left py-1.5 font-medium">Mode</th>
                              <th className="text-right py-1.5 font-medium">Montant TTC</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(z.by_method).map(([m, amt]) => (
                              <tr key={m} className="border-b border-stone-100">
                                <td className="py-1.5">{m}</td>
                                <td className="py-1.5 text-right font-semibold">{fmtEur(amt)}&euro;</td>
                              </tr>
                            ))}
                            <tr className="font-bold border-t border-stone-300">
                              <td className="py-1.5">TOTAL Z</td>
                              <td className="py-1.5 text-right">{fmtEur(z.ttc)}&euro;</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
                {Object.keys(report.daily_z || {}).length === 0 && (
                  <p className="text-center text-stone-400 py-8">Aucun Z pour cette periode</p>
                )}
              </div>
            )}

            {/* Sessions */}
            {tab === 'sessions' && (
              <div className="space-y-3" data-testid="sessions-section">
                <h3 className="text-sm font-bold text-stone-700 uppercase tracking-wider">Detail par session de table</h3>
                {(report.sessions || []).map((s, idx) => {
                  const key = `${s.table_number}-${s.date}-${idx}`;
                  const expanded = expandedSessions[key];
                  const isSplit = s.payments.length > 1;
                  return (
                    <div key={key} className="bg-white border border-stone-200 rounded-xl shadow-sm overflow-hidden">
                      <div className="flex items-center gap-4 px-5 py-3 cursor-pointer hover:bg-stone-50 transition-colors"
                        onClick={() => toggleSession(key)} data-testid={`session-${key}`}>
                        <div className="w-10 h-10 bg-[#f5f0ea] rounded-xl flex items-center justify-center text-sm font-bold text-[#3d2817]">
                          T{s.table_number}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-stone-800 text-sm">Table {s.table_number}</span>
                            <span className="text-xs text-stone-400">{fmtDate(s.date)}</span>
                            <Badge variant="outline" className="text-[10px] gap-1"><Users size={10} /> {s.covers} couv.</Badge>
                            {isSplit && <Badge className="bg-blue-100 text-blue-700 text-[10px] border-blue-200">Paiement fractionne ({s.payments.length})</Badge>}
                            {s.methods.map(m => {
                              const I = METHOD_ICONS[m] || Receipt;
                              return <Badge key={m} variant="outline" className="text-[10px] gap-1"><I size={10} /> {m}</Badge>;
                            })}
                          </div>
                        </div>
                        <div className="text-right mr-2 flex-shrink-0">
                          <p className="font-bold text-[#8B0000]">{fmtEur(s.total_ttc)}&euro;</p>
                          <p className="text-[10px] text-stone-400">Net: {fmtEur(s.total_ttc)}</p>
                        </div>
                        {expanded ? <ChevronUp size={16} className="text-stone-400" /> : <ChevronDown size={16} className="text-stone-400" />}
                      </div>

                      {expanded && (
                        <div className="border-t border-stone-100 bg-stone-50/50">
                          {/* Items summary */}
                          <div className="px-5 py-3 border-b border-stone-100">
                            <p className="text-[10px] font-bold text-stone-500 uppercase mb-1.5">Articles commandes</p>
                            <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-stone-600">
                              {s.items.map((item, i) => (
                                <span key={i}>{item.quantity}x {item.name} <span className="text-stone-400">({fmtEur(parseFloat(item.price) * item.quantity)}&euro;)</span></span>
                              ))}
                            </div>
                          </div>
                          {/* Individual payments */}
                          {s.payments.map(p => (
                            <div key={p.id} className="flex items-center gap-3 px-5 py-2.5 border-b border-stone-100 last:border-b-0 text-xs">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-[10px] font-bold text-[#3d2817]">{p.receipt_number}</span>
                                  <span className="text-stone-400">{fmtTime(p.created_at)}</span>
                                  <Badge variant="outline" className="text-[10px]">{p.method}</Badge>
                                  <span className="text-stone-400">par {p.created_by_name}</span>
                                </div>
                                <div className="flex gap-3 text-[10px] text-stone-500 mt-0.5">
                                  <span>Net: {fmtEur(p.amount)}&euro;</span>
                                </div>
                              </div>
                              <span className="font-bold text-sm text-[#8B0000]">{fmtEur(p.amount)}&euro;</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                {(report.sessions || []).length === 0 && (
                  <p className="text-center text-stone-400 py-8">Aucune session pour cette periode</p>
                )}
              </div>
            )}

            {/* Legal footer */}
            <div className="mt-8 pt-4 border-t-2 border-stone-300 text-[10px] text-stone-400 space-y-1" data-testid="legal-footer">
              <p className="font-semibold text-stone-500">Mentions legales - Guyane francaise</p>
              <p>Logiciel de caisse conforme a la loi anti-fraude (Art. 88 LOI n2015-1785 du 29/12/2015)</p>
              <p>Exoneration de TVA - Art. 294 du Code General des Impots (DOM)</p>
              <p>Conservation obligatoire des donnees comptables : 10 ans (Art. L102B LPF)</p>
              <p>Factures numerotees sequentiellement et de maniere chronologique (Art. 242 nonies A CGI)</p>
              {legal.siret && <p>SIRET: {legal.siret} {legal.tva_intra && `| TVA Intracommunautaire: ${legal.tva_intra}`} {legal.ape && `| Code APE: ${legal.ape}`}</p>}
              <p className="text-stone-300 mt-2">Rapport genere le {new Date().toLocaleString('fr-FR', { timeZone: 'America/Cayenne' })} (UTC-3 Guyane)</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AccountingPage;
